<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
);

const props = defineProps({
  periods: {
    type: Array,
    default: () => [],
  },
  metric: {
    type: String,
    default: "hourly_rate",
  },
  metricLabel: {
    type: String,
    default: "Hourly rate",
  },
});

const darkMode = ref(false);
let colourSchemeQuery;

const updateColourScheme = (event) => {
  darkMode.value = event.matches;
};

onMounted(() => {
  colourSchemeQuery = window.matchMedia("(prefers-color-scheme: dark)");
  darkMode.value = colourSchemeQuery.matches;
  colourSchemeQuery.addEventListener("change", updateColourScheme);
});

onBeforeUnmount(() => {
  colourSchemeQuery?.removeEventListener("change", updateColourScheme);
});

const currencyFormatter = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const toDateInputValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const prefixedDate = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (prefixedDate) {
      return prefixedDate[1];
    }
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toISOString().slice(0, 10);
};

const formatDate = (value) => {
  const normalized = toDateInputValue(value);
  if (!normalized) return "Open";
  const [year, month, day] = normalized.split("-");
  return `${day}/${month}/${year}`;
};

const normalizedPeriods = computed(() =>
  [...props.periods]
    .map((period) => ({
      ...period,
      starts_at: toDateInputValue(period?.starts_at),
      ends_at: toDateInputValue(period?.ends_at),
    }))
    .filter((period) => period.starts_at)
    .sort((a, b) => a.starts_at.localeCompare(b.starts_at)),
);

const chartData = computed(() => ({
  labels: normalizedPeriods.value.map((period) => formatDate(period.starts_at)),
  datasets: [
    {
      label: props.metricLabel,
      data: normalizedPeriods.value.map(
        (period) => Number(period?.[props.metric] ?? 0) / 100,
      ),
      borderColor: "rgba(14, 116, 144, 0.95)",
      backgroundColor: "rgba(14, 116, 144, 0.12)",
      pointBackgroundColor: "rgba(14, 116, 144, 0.95)",
      pointBorderColor: "#ffffff",
      pointRadius: 4,
      pointHoverRadius: 6,
      tension: 0.25,
      stepped: "before",
      fill: true,
    },
  ],
}));

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: "index",
    intersect: false,
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: {
        color: darkMode.value ? "#cbd5e1" : "#64748b",
        maxRotation: 0,
        minRotation: 0,
      },
    },
    y: {
      beginAtZero: true,
      grid: {
        color: darkMode.value
          ? "rgba(148, 163, 184, 0.18)"
          : "rgba(148, 163, 184, 0.25)",
        drawBorder: false,
      },
      ticks: {
        color: darkMode.value ? "#cbd5e1" : "#64748b",
        callback: (value) => currencyFormatter.format(value),
      },
    },
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        title: (items) => {
          const period = normalizedPeriods.value[items?.[0]?.dataIndex ?? -1];
          if (!period) return "";

          return `${formatDate(period.starts_at)} - ${
            period.ends_at ? formatDate(period.ends_at) : "Open"
          }`;
        },
        label: (context) =>
          `${props.metricLabel}: ${currencyFormatter.format(context.parsed.y ?? 0)}`,
      },
    },
  },
}));
</script>

<template>
  <div class="w-full">
    <Line :data="chartData" :options="chartOptions" class="h-72" />
  </div>
</template>
